import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ReportExportService } from '../../report-export.service';
import { ReportContext, ReportDefinition } from '../../report.models';
import { toEnglishDigits } from '../../report.utils';
import { BusReportApiService } from './bus-report-api.service';
import {
  BusApiResult, BusLineOption, BusOption, BusReportColumn, BusReportCredentials,
  BusReportFilters, BusReportRow, BusRequestStage, BusStopOption,
} from './bus-report.models';
import { buildBusReportPayload, buildBusSearchIndex, BUS_PAGE_SIZE, BUS_PAGE_SIZE_OPTIONS, getBusReportColumns } from './bus-report.utils';

@Component({
  selector: 'app-bus-report',
  templateUrl: './bus-report.component.html',
})
export class BusReportComponent implements OnChanges, OnDestroy {
  @Input() context!: ReportContext;
  @Input() definition!: ReportDefinition;

  readonly filterForm = new FormGroup({
    bus: new FormControl(''),
    line: new FormControl(''),
    stop: new FormControl(''),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
  });
  buses: BusOption[] = [];
  lines: BusLineOption[] = [];
  stops: BusStopOption[] = [];
  columns: BusReportColumn[] = [];
  rows: BusReportRow[] = [];
  filteredRows: BusReportRow[] = [];
  pagedRows: BusReportRow[] = [];
  filtersOpen = true;
  searchText = '';
  currentPage = 1;
  pageSize = BUS_PAGE_SIZE;
  readonly pageSizeOptions = BUS_PAGE_SIZE_OPTIONS;
  totalPages = 1;
  loading = false;
  hasError = false;
  hasRequestedData = false;
  private failedStage: BusRequestStage | null = null;
  private requestVersion = 0;
  private lastFilterKey = '';
  private readonly filterSubscription: Subscription;

  constructor(
    private readonly api: BusReportApiService,
    private readonly exportService: ReportExportService,
    private readonly gurdService: GurdServiceService,
    private readonly notificationService: NotificationService,
    private readonly otherService: OtherService,
    private readonly router: Router,
    private readonly location: Location,
  ) {
    this.lastFilterKey = this.filterKey();
    this.filterSubscription = this.filterForm.valueChanges.subscribe(() => {
      if (this.filterKey() !== this.lastFilterKey) this.resetResults();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes['context'] && !changes['definition']) || !this.context || !this.definition?.busMode) return;
    this.requestVersion++;
    this.setLoading(false);
    this.columns = getBusReportColumns(this.definition.busMode);
    this.buses = [];
    this.lines = [];
    this.stops = [];
    this.filtersOpen = true;
    this.failedStage = null;
    const today = toEnglishDigits(this.otherService.getCurrentDate());
    this.filterForm.setValue({
      bus: '', line: '', stop: '',
      startDate: this.isBusPresence ? `${today.slice(0, 8)}01` : today,
      endDate: today,
    }, { emitEvent: false });
    this.resetResults();
    void this.loadOptions();
  }

  ngOnDestroy(): void {
    this.requestVersion++;
    this.filterSubscription.unsubscribe();
  }

  async loadOptions(): Promise<void> {
    if (this.loading) return;
    if (this.isBusPresence) {
      await this.runRequest('options', () => this.api.loadBuses(this.credentials), (buses) => { this.buses = buses; });
    } else {
      await this.runRequest('options', () => this.api.loadLines(this.credentials), (lines) => { this.lines = lines; });
    }
  }

  async onLineChange(): Promise<void> {
    if (this.loading) return;
    this.stops = [];
    if (this.failedStage === 'stops') this.failedStage = null;
    this.filterForm.controls.stop.setValue('', { emitEvent: false });
    this.resetResults();
    await this.loadStops();
  }

  async loadStops(): Promise<void> {
    if (this.loading || !this.filters.line) return;
    await this.runRequest('stops', () => this.api.loadStops(this.filters.line, this.credentials), (stops) => { this.stops = stops; });
  }

  async loadReport(): Promise<void> {
    if (this.loading || !this.canLoadReport || !this.definition.endpoint) return;
    const filters = this.filters;
    if (!this.isBusPresence && toEnglishDigits(filters.startDate) > toEnglishDigits(filters.endDate)) {
      this.notificationService.showError('تاریخ پایان نباید قبل از تاریخ شروع باشد');
      return;
    }
    this.hasRequestedData = true;
    this.filtersOpen = false;
    const payload = buildBusReportPayload(this.definition.busMode!, filters, this.credentials);
    await this.runRequest('report', () => this.api.loadRows(this.definition.endpoint!, payload), (rows) => {
      this.rows = rows.map((row) => ({ ...row, searchIndex: buildBusSearchIndex(row) }));
      this.refreshTable(true);
    });
  }

  async retry(): Promise<void> {
    if (this.loading) return;
    if (this.failedStage === 'options') await this.loadOptions();
    else if (this.failedStage === 'stops') await this.loadStops();
    else await this.loadReport();
  }

  toggleFilters(): void {
    if (!this.loading) this.filtersOpen = !this.filtersOpen;
  }

  onSearchChange(): void {
    this.refreshTable(true);
  }

  clearSearch(): void {
    this.searchText = '';
    this.refreshTable(true);
  }

  onPageSizeChange(pageSize: number): void {
    if (!Number.isFinite(pageSize) || pageSize <= 0) return;
    this.pageSize = pageSize;
    this.refreshTable(true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.refreshTable();
  }

  cellValue(row: BusReportRow, column: BusReportColumn): string {
    if (column.format === 'plate') return row.strVehicleNo ? this.otherService.returnCorrectPlate(row.strVehicleNo) : '';
    return String(row[column.field] ?? '');
  }

  exportExcel(): void {
    if (this.loading || this.hasError || !this.filteredRows.length) return;
    this.exportService.exportRows(
      this.definition.fileName,
      ['#', ...this.columns.map((column) => column.title)],
      this.filteredRows.map((row, index) => [index + 1, ...this.columns.map((column) => this.cellValue(row, column))]),
    );
  }

  goBack(): void {
    this.location.back();
  }

  get isBusPresence(): boolean { return this.definition?.busMode === 'bus-presence'; }
  get filters(): BusReportFilters { return this.filterForm.getRawValue() as BusReportFilters; }
  get hasActiveFilters(): boolean { return !!this.searchText.trim(); }
  get canLoadReport(): boolean {
    const filters = this.filters;
    return !!filters.startDate && (this.isBusPresence
      ? this.buses.some((bus) => String(bus.strUnitID) === filters.bus)
      : !!filters.endDate && this.stops.some((stop) => String(stop.iBusStop) === filters.stop));
  }
  get filterCount(): number {
    const filters = this.filters;
    return (this.isBusPresence ? [filters.bus, filters.startDate] : [filters.line, filters.stop, filters.startDate, filters.endDate])
      .filter(Boolean).length;
  }
  get subtitle(): string {
    return this.isBusPresence ? `تاریخ ${this.filters.startDate || '-'}`
      : `از تاریخ ${this.filters.startDate || '-'} الی ${this.filters.endDate || '-'}`;
  }
  get searchPlaceholder(): string { return `جستجو در جدول: ${(this.definition.searchFields || []).join('، ')}...`; }
  get printSectionId(): string { return `bus-report-print-${this.definition.busMode}`; }
  get tableId(): string { return `bus-report-${this.definition.busMode}`; }
  get emptyTitle(): string {
    if (this.hasActiveFilters) return 'با این فیلترها نتیجه‌ای پیدا نشد!';
    if (!this.hasRequestedData) return this.isBusPresence ? 'ابتدا اتوبوس و تاریخ را انتخاب کنید' : 'ابتدا خط، ایستگاه و بازهٔ تاریخ را انتخاب کنید';
    return 'اطلاعاتی برای نمایش وجود ندارد';
  }
  get emptyDescription(): string {
    if (this.hasActiveFilters) return 'عبارت جستجو را تغییر دهید و دوباره تلاش کنید.';
    if (!this.hasRequestedData) return 'پس از تعیین فیلتر، دکمهٔ بررسی گزارش را بزنید.';
    return this.isBusPresence ? 'برای این اتوبوس در تاریخ انتخاب‌شده، حضوری در ایستگاه‌ها ثبت نشده است.'
      : 'در بازهٔ انتخاب‌شده، حضور اتوبوسی در این ایستگاه ثبت نشده است.';
  }

  private get credentials(): BusReportCredentials {
    return {
      iSystemUser: this.gurdService.decrypt(localStorage.getItem('ID')!),
      strSession: this.gurdService.decrypt(localStorage.getItem('Session')!),
    };
  }

  private async runRequest<T>(stage: BusRequestStage, request: () => Promise<BusApiResult<T>>, onSuccess: (rows: T[]) => void): Promise<void> {
    if (this.loading) return;
    const version = ++this.requestVersion;
    const loginReport = stage === 'options' ? (this.isBusPresence ? 'bus-list' : 'lines-list')
      : stage === 'stops' ? 'set-down' : this.definition.loginReport;
    this.setLoading(true);
    this.hasError = false;
    this.failedStage = null;
    try {
      const result = await request();
      if (version !== this.requestVersion) return;
      if (!Array.isArray(result)) {
        if (result.ISessionExpired || result.ISSessionExpired) {
          this.notificationService.showError('لطفا وارد سیستم شوید');
          this.otherService.removeLocalStorage();
          void this.router.navigate(['login'], { queryParams: { report: loginReport } });
          return;
        }
        throw new Error(result.strError || 'دریافت اطلاعات با خطا مواجه شد');
      }
      onSuccess(result);
    } catch (error: unknown) {
      if (version !== this.requestVersion) return;
      this.rows = [];
      this.refreshTable(true);
      this.hasError = true;
      this.failedStage = stage;
      if (stage !== 'report') this.filtersOpen = true;
      this.notificationService.showError(error instanceof Error ? error.message : 'دریافت اطلاعات با خطا مواجه شد');
    } finally {
      if (version === this.requestVersion) this.setLoading(false);
    }
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    if (loading) this.filterForm.disable({ emitEvent: false });
    else this.filterForm.enable({ emitEvent: false });
  }

  private resetResults(): void {
    this.lastFilterKey = this.filterKey();
    this.rows = [];
    this.searchText = '';
    this.hasRequestedData = false;
    // Preserve failures in the option lists until the failed request is retried.
    this.hasError = this.failedStage === 'options' || this.failedStage === 'stops';
    this.refreshTable(true);
  }

  private filterKey(): string {
    const filters = this.filters;
    return JSON.stringify({
      ...filters,
      startDate: toEnglishDigits(filters.startDate),
      endDate: toEnglishDigits(filters.endDate),
    });
  }

  private refreshTable(resetPage = false): void {
    if (resetPage) this.currentPage = 1;
    const query = toEnglishDigits(this.searchText).trim().toLowerCase();
    this.filteredRows = query ? this.rows.filter((row) => row.searchIndex?.includes(query)) : this.rows;
    this.totalPages = Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    const offset = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(offset, offset + this.pageSize);
  }
}
